/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable, OnDestroy } from '@angular/core';
import { io, Socket } from 'socket.io-client';
import { EncryptionService } from './encryption.service';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class SocketsService implements OnDestroy {
  public socket!: Socket;
  private isConnected = false;
  constructor(private _encryptionService: EncryptionService) {}

  initSocket(): void {
    if (!this.socket && !this.isConnected) {
      const token = this._encryptionService.decryptLocalStorage(
        environment.storageKey
      );
      if (!token) {
        console.warn('Socket not initialized: No token found.');
        return;
      }

      this.socket = io(environment.socketUrl, {
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 3000,
        reconnectionAttempts: Infinity,
        query: { token },
        autoConnect: true,
      });

      this.setupListeners();
    }
  }

  private setupListeners(): void {
    if (!this.socket) return;

    this.socket.on('connect', () => {
      console.log('Connected to WebSocket server');
      this.isConnected = true;
    });

    this.socket.on('connect_error', error => {
      console.error('WebSocket Connection Error:', error);
    });

    this.socket.on('reconnect_attempt', (attempt: number) => {
      console.log('Reconnecting... attempt', attempt);
    });

    this.socket.on('reconnect', (attemptNumber: number) => {
      console.log('Reconnected after', attemptNumber, 'attempt(s)');
      this.isConnected = true;
    });

    this.socket.on('disconnect', (reason: string) => {
      console.warn('Disconnected:', reason);
      this.isConnected = false;

      // If server forced the disconnect, attempt manual reconnect
      if (reason === 'io server disconnect') {
        // Server disconnected us: need to call connect() to reconnect
        console.log('Server forced disconnect — attempting to reconnect...');
        setTimeout(() => this.socket?.connect(), 500);
      } else if (reason === 'io client disconnect') {
        // Client-side disconnect happened somewhere else; attempt reconnect
        // only if we still have a socket instance (helps recover from accidental disconnect)
        console.log('Client disconnect detected — attempting reconnect...');
        setTimeout(() => {
          if (this.socket && !this.socket.connected) {
            this.socket.connect();
          }
        }, 1000);
      } else {
        // for network issues or other reasons, socket.io already tries to reconnect
      }
    });
  }

  notificationListner(): Observable<any> {
    return new Observable<any>(observer => {
      this.socket.on('notification', (data: any) => {
        observer.next(data);
      });
      return () => {
        this.socket.disconnect();
      };
    });
  }

  notificationEnqueriesListner(): Observable<any> {
    return new Observable<any>(observer => {
      this.socket.on('enquiry-notification', (data: any) => {
        observer.next(data);
      });
      return () => {
        this.socket.disconnect();
      };
    });
  }

  disconnect(): void {
    if (this.socket) {
      this.socket.removeAllListeners(); // Prevent memory leaks
      this.socket.disconnect();
      this.isConnected = false;
      console.log('Socket disconnected.');
    }
  }

  ngOnDestroy(): void {
    this.disconnect();
  }
}
